// assignment-chart.component.ts
import * as angular from "angular";
import { Group } from "../../models/group.model";
import { Assignment } from "../../models/assignment.model";
import { GroupDataProvider } from "../../data-provider/group.dp";
import { EffortDataProvider } from "../../data-provider/effort.dp";
import { CampaignDataProvider } from "../../data-provider/campaign.dp";
import { PermissionCheck } from "../../shared/permission";
import { DialogUtils } from "../../helper/dialog.helper";
import { ToastUtils } from "../../helper/toast.helper";
import {
    AssignmentLane,
    GanttAssignment,
    GanttList,
    GanttTimelineUnit,
} from "../../models/gantt.model";
import { AssignmentFilter } from "../../models/assignment-filter.model";
import { Team } from "../../models/team.model";

let GanttChartComponent = {
    selector: "vcAssignmentChart",
    templateUrl: "/angular/components/planner/assignment-chart.component.html",
    bindings: {},
    controller: class GanttChartController {
        public lists: Group[] = [];
        public viewMode: "days" | "weeks" | "months" | "quarters" = "days";
        public timelineStart?: Date;
        public timelineEnd?: Date;
        public lanes: any = {};
        public groupLists: boolean = false;

        public originalGanttLists: GanttList[];
        public ganttLists: GanttList[] = [];
        public timeline: GanttTimelineUnit[] = [];
        public assignmentLanes: Map<string, AssignmentLane[]> = new Map();
        public today = new Date();
        public cellWidth: number = 0;
        public filters: AssignmentFilter;
        public localInfo: any = {};
        public campaignid: any;
        public readonly CELL_WIDTH = 60;
        public readonly ASSIGNMENT_HEIGHT = 20;
        public readonly ASSIGNMENT_MARGIN = 4;
        public readonly MIN_ROW_HEIGHT = 50;
        public readonly LIST_COLUMN_WIDTH = 400;
        public loading = false;
        public error = false;
        private isScrolling = false;
        public scrollViewOptions: any = {
            viewportOffset: [1000, 1000000, 1000, 1000000],
        };
        public flatAssignments: Array<GanttAssignment & { list: GanttList }> =
            [];
        public flatLaneAssignments: Array<
            AssignmentLane & { list: GanttList }
        > = [];
        // Dependencies
        private $scope: any;
        private $timeout: any;
        private $interval: any;
        private $window: any;
        private localStorageService: any;
        private groupDataProvider: GroupDataProvider;
        private effortDataProvider: EffortDataProvider;
        private permissionProvider: PermissionCheck;
        private dialog: DialogUtils;
        private modelsUtils: any;
        private toast: ToastUtils;

        constructor(
            $scope: any,
            $timeout: any,
            $interval: any,
            $window: any,
            GroupDataProvider: GroupDataProvider,
            EffortDataProvider: EffortDataProvider,
            CampaignDataProvider: CampaignDataProvider,
            PermissionCheck: PermissionCheck,
            DialogUtils: DialogUtils,
            ToastUtils: ToastUtils,
            ModelsUtils: any,
            localStorageService: any
        ) {
            this.$scope = $scope;
            this.$timeout = $timeout;
            this.$interval = $interval;
            this.$window = $window;
            this.groupDataProvider = GroupDataProvider;
            this.effortDataProvider = EffortDataProvider;
            this.permissionProvider = PermissionCheck;
            this.dialog = DialogUtils;
            this.toast = ToastUtils;
            this.modelsUtils = ModelsUtils;
            this.localStorageService = localStorageService;
            let campaign = CampaignDataProvider.getActiveCampaign();
            this.campaignid = campaign.campaignid;
            this.getFilters();
        }

        $onInit() {
            // Watch for view mode changes
            this.$scope.$watch(
                () => this.viewMode,
                (newMode: string, oldMode: string) => {
                    if (newMode !== oldMode && this.ganttLists.length > 0) {
                        this.calculateCellWidth();
                        this.generateTimeline();
                        this.calculateAssignmentLanes();
                        this.saveLocalStorage();
                    }
                }
            );

            this.effortDataProvider.getSelectedEffort().then((effort: any) => {
                if (effort) {
                    this.groupDataProvider.setEffort(effort);
                }
                this.initializeData();
            });
        }

        private initializeData(): void {
            if (!this.isDataProviderReady()) {
                return;
            }

            this.refreshData();
        }

        private processData(): void {
            this.ganttLists = this.transformGroupsToGanttLists();
            if (this.filters) {
                this.applyLocalFilters(this.filters, this.ganttLists);
            }
            // Create flat array with list references
            this.flatAssignments = this.ganttLists
                .flatMap((list) =>
                    list.ganttAssignments.map((assignment) => ({
                        ...assignment,
                        list: list, // or just list.gid if you only need the ID
                    }))
                )
                .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
            this.calculateCellWidth();
            this.generateTimeline();
            this.calculateAssignmentLanes();
        }

        public applyLocalFilters(filters: any, ganttLists: GanttList[]) {
            if (!filters || !ganttLists || ganttLists.length === 0) {
                return;
            }

            // Extract filter criteria
            const dateStart = filters.dateRange?.dateStart
                ? new Date(filters.dateRange.dateStart)
                : null;
            const dateEnd = filters.dateRange?.dateEnd
                ? new Date(filters.dateRange.dateEnd)
                : null;
            const statusFilters = filters.status || [];
            const teamFilters = filters.teams || [];
            const userFilters = filters.users || [];

            console.log("Applying filters:", {
                dateRange: { start: dateStart, end: dateEnd },
                status: statusFilters,
                teams: teamFilters.map((t) => ({ tid: t.tid, name: t.tname })),
                users: userFilters,
            });

            this.ganttLists = this.ganttLists.map((list) => {
                // Filter assignments to only include those that match ALL active filters
                const filteredAssignments = list.ganttAssignments.filter(
                    (assignment) => {
                        // Date range filter - if dateStart and dateEnd are provided
                        if (dateStart && dateEnd) {
                            const assignmentStart = new Date(
                                assignment.startDate
                            );
                            const assignmentEnd = new Date(assignment.endDate);

                            // Assignment must overlap with the filter date range
                            const hasDateOverlap =
                                assignmentStart <= dateEnd &&
                                assignmentEnd >= dateStart;
                            if (!hasDateOverlap) {
                                return false; // Assignment doesn't match date filter
                            }
                        }

                        // Status filter - if statusFilters array has items
                        if (statusFilters.length > 0) {
                            const hasMatchingStatus = statusFilters.includes(
                                assignment.status
                            );
                            if (!hasMatchingStatus) {
                                return false; // Assignment doesn't match status filter
                            }
                        }

                        // Team filter - if teamFilters array has items
                        if (teamFilters.length > 0) {
                            const teamIds = teamFilters.map(
                                (team) =>
                                    team.tid?.toString() || team.id?.toString()
                            );
                            const teamNames = teamFilters.map(
                                (team) => team.tname || team.name
                            );

                            const hasMatchingTeam =
                                assignment.assignedTeams?.some(
                                    (assignedTeam) => {
                                        // Check if assigned team name matches any filter team name
                                        if (teamNames.includes(assignedTeam)) {
                                            return true;
                                        }

                                        // Check if assignment's original teams match any filter team IDs
                                        return assignment.originalAssignment?.teams?.some(
                                            (team) =>
                                                teamIds.includes(
                                                    team.tid?.toString() ||
                                                        team.id?.toString()
                                                )
                                        );
                                    }
                                );

                            if (!hasMatchingTeam) {
                                return false; // Assignment doesn't match team filter
                            }
                        }

                        // User filter - if userFilters array has items
                        if (userFilters.length > 0) {
                            const userIds = userFilters.map(
                                (user) =>
                                    user.uid?.toString() ||
                                    user.id?.toString() ||
                                    "unassigned"
                            );
                            const userNames = userFilters.map(
                                (user) => user.uname || user.name
                            );
                            if (
                                userIds.includes("unassigned") &&
                                assignment.assignedUsers.length +
                                    assignment.assignedTeams.length ==
                                    0
                            ) {
                                return true;
                            }
                            const hasMatchingUser =
                                assignment.assignedUsers?.some(
                                    (assignedUser) => {
                                        // Check if assigned user name matches any filter user name
                                        if (userNames.includes(assignedUser)) {
                                            return true;
                                        }

                                        // Check if assignment's original users match any filter user IDs
                                        return assignment.originalAssignment?.users?.some(
                                            (user) =>
                                                userIds.includes(
                                                    user.uid?.toString() ||
                                                        user.id?.toString()
                                                )
                                        );
                                    }
                                );
                            const hasMatchingTeam =
                                assignment.originalAssignment?.teams?.some(
                                    (team) =>
                                        team.users?.some((user) => {
                                            return userIds.includes(
                                                user.uid?.toString() ||
                                                    user.id?.toString()
                                            );
                                        })
                                );

                            if (!(hasMatchingTeam || hasMatchingUser)) {
                                return false; // Assignment doesn't match user filter
                            }
                        }

                        // If we reach here, the assignment passes all active filters
                        return true;
                    }
                );

                // Create the filtered list data
                const filteredListData = {
                    ...list,
                    ganttAssignments: filteredAssignments,
                } as GanttList;

                // Recalculate overall progress based on filtered assignments
                filteredListData.overallProgress =
                    this.calculateOverallProgress(filteredAssignments);

                return filteredListData;
            });

            console.log(
                `Filtered result: ${
                    this.ganttLists.length
                } lists with ${this.getTotalAssignmentsCount()} total assignments`
            );
        }

        // Alternative version if you want to store original data separately
        public applyLocalFiltersWithBackup(filters: any): void {
            if (!this.originalGanttLists) {
                this.originalGanttLists = [...this.ganttLists];
            }

            this.ganttLists = [...this.originalGanttLists];

            this.applyLocalFilters(filters, this.ganttLists);
        }

        // Method to clear all filters and restore original data
        public clearLocalFilters() {
            if (this.originalGanttLists) {
                this.ganttLists = [...this.originalGanttLists];
                this.generateTimeline();
                this.calculateAssignmentLanes();
                console.log("Filters cleared, restored original data");
            }
        }

        // Add this property to your class to store original data
        // private originalGanttLists: GanttList[] = [];
        public refreshData(): void {
            this.lists = [];
            this.ganttLists = [];
            this.timeline = [];
            this.assignmentLanes.clear();
            this.groupDataProvider.clearAll();

            if (!this.isDataProviderReady()) {
                this.loading = false;
                this.error = false;
                return;
            }

            this.loadLists();
        }

        private isDataProviderReady(): boolean {
            if (!this.groupDataProvider) {
                return false;
            }

            if (!this.groupDataProvider.effort) {
                return false;
            }

            return true;
        }

        public syncWithDataProvider(): void {
            this.loading = this.groupDataProvider.loading;
            this.error = this.groupDataProvider.error;

            if (this.groupDataProvider.assignmentGroups) {
                this.lists = this.groupDataProvider.assignmentGroups;
                this.processData();
            }
        }

        private loadLists(): void {
            this.loading = true;
            this.error = false;

            if (
                this.groupDataProvider.assignmentGroups &&
                this.groupDataProvider.assignmentGroups.length > 0
            ) {
                this.lists = this.groupDataProvider.assignmentGroups;
                this.processData();
                this.loading = false;
                return;
            }
            const loadPromise = this.groupDataProvider.getAllAssignmentGroups();

            loadPromise
                .then((groups: Group[]) => {
                    this.lists = groups || [];

                    this.processData();
                    this.loading = false;
                })
                .catch((error: any) => {
                    if (error !== "user cancelled") {
                        this.error = true;
                        this.toast.error("Failed to load lists");
                    }
                    this.loading = false;
                });
        }

        private transformGroupsToGanttLists(): GanttList[] {
            this.timelineEnd = this.getLatestDate(this.lists);
            return this.lists.map((group) => {
                const ganttAssignments = this.transformAssignments(
                    group.assignments
                );
                const ganttList = {
                    ...group,
                    originalList: group,
                    ganttAssignments,
                    overallProgress:
                        this.calculateOverallProgress(ganttAssignments),
                } as GanttList;
                return ganttList;
            });
        }

        private transformAssignments(
            assignments: Assignment[]
        ): GanttAssignment[] {
            if (!assignments || !Array.isArray(assignments)) {
                return [];
            }

            const now = new Date();
            const ganttAssignments = assignments
                .filter((assignment) => {
                    const isValid =
                        assignment && assignment.status !== "Deleted";
                    return isValid;
                })
                .map((assignment, index) => {
                    const startDate = this.getAssignmentStartDate(assignment);
                    const endDate = this.getAssignmentEndDate(assignment);

                    // Calculate actual status based on dates
                    let actualStatus;
                    if (endDate < now) {
                        actualStatus = "Done";
                    } else if (startDate <= now && endDate >= now) {
                        actualStatus = "Active";
                    } else {
                        actualStatus = "Scheduled";
                    }

                    const ganttAssignment = {
                        id:
                            assignment.id ||
                            `assignment-${Date.now()}-${index}`,
                        name: this.getAssignmentName(assignment, index),
                        startDate,
                        endDate,
                        progress: this.calculateAssignmentProgress(
                            actualStatus,
                            startDate,
                            endDate
                        ),
                        status: actualStatus,
                        assignedTeams: assignment.teams || [],
                        assignedUsers: assignment.users || [],
                        originalAssignment: assignment,
                    };

                    return ganttAssignment;
                });

            return ganttAssignments;
        }

        private getAssignmentStartDate(assignment: Assignment): Date {
            if (assignment.starttimestamp) {
                const date = new Date(
                    assignment.starttimestamp.replace(/-/g, "/")
                );
                return isNaN(date.getTime()) ? new Date() : date;
            }
            if (assignment.start && assignment.start.date) {
                return new Date(assignment.start.date);
            }
            return new Date();
        }

        private getAssignmentEndDate(assignment: Assignment): Date {
            if (assignment.endtimestamp) {
                const date = new Date(
                    assignment.endtimestamp.replace(/-/g, "/")
                );
                if (!isNaN(date.getTime())) {
                    return date;
                }
            }
            if (assignment.end && assignment.end.date) {
                return new Date(assignment.end.date);
            }

            const endDate = new Date(this.timelineEnd);
            endDate.setDate(endDate.getDate());
            return endDate;
        }

        private getAssignmentName(
            assignment: Assignment,
            index: number
        ): string {
            if (assignment["assignmentname"])
                return assignment["assignmentname"];
            return `Assignment`;
        }

        private calculateAssignmentProgress(
            assignment: Assignment,
            startDate: Date,
            endDate: Date
        ): number {
            if (assignment.status === "Done") return 100;
            if (assignment.status === "Scheduled") return 0;

            if (assignment.status === "Active") {
                const now = new Date();
                const totalDuration = endDate.getTime() - startDate.getTime();
                const elapsed = now.getTime() - startDate.getTime();

                if (elapsed <= 0) return 0;
                if (elapsed >= totalDuration) return 95;

                return Math.round((elapsed / totalDuration) * 85);
            }

            return 0;
        }

        private getAssignedTeamNames(assignment: Assignment): Team[] {
            if (!assignment.teams || !Array.isArray(assignment.teams)) {
                return [];
            }

            return assignment.teams;
        }
        private calculateCellWidth() {
            this.cellWidth;
            switch (this.viewMode) {
                case "days":
                    this.cellWidth = this.CELL_WIDTH;
                    break;
                case "weeks":
                    this.cellWidth = this.CELL_WIDTH * 2;
                    break;
                case "months":
                    this.cellWidth = this.CELL_WIDTH * 2;
                    break;
                case "quarters":
                    this.cellWidth = this.CELL_WIDTH * 3;
                    break;
            }
        }
        private calculateOverallProgress(
            assignments: GanttAssignment[]
        ): number {
            if (!assignments.length) return 0;

            // Calculate weighted average based on assignment duration
            let totalProgress = 0;
            let totalWeight = 0;

            assignments.forEach((assignment) => {
                const duration =
                    assignment.endDate.getTime() -
                    assignment.startDate.getTime();
                const weight = Math.max(duration, 1); // Prevent division by zero
                totalProgress += assignment.progress * weight;
                totalWeight += weight;
            });

            return totalWeight > 0
                ? Math.round(totalProgress / totalWeight)
                : 0;
        }

        private generateTimeline(): void {
            this.timelineEnd = this.getLatestDate(this.lists);
            const allAssignments = this.ganttLists.flatMap(
                (list) => list.ganttAssignments
            );
            if (!allAssignments.length) {
                this.timeline = [];
                return;
            }

            const datedAssignments = allAssignments.filter(
                (assignment) => !!assignment.endDate || !!assignment.startDate
            );

            let start: Date;
            let end: Date;

            if (datedAssignments.length > 0) {
                start =
                    this.timelineStart ||
                    this.getEarliestDate(datedAssignments);
                end = this.timelineEnd;
            } else {
                start = this.timelineStart || new Date();
                end =
                    this.timelineEnd ||
                    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
            }
            this.timeline = [];
            const current = new Date(start);
            const dayLetters = ["S", "M", "T", "W", "T", "F", "S"];
            while (current <= end) {
                const dayOfWeek = current.getDay();
                const timelineUnit = {
                    date: new Date(current),
                    label: this.formatTimelineLabel(new Date(current)),
                    isToday: this.isSameDate(current, this.today),
                    dayLetter: dayLetters[dayOfWeek],
                };

                this.timeline.push(timelineUnit);
                this.incrementDate(current);
            }
        }

        private calculateAssignmentLanes(): void {
            this.assignmentLanes.clear();

            const lanes: Array<AssignmentLane & { list: GanttList }> = [];
            this.flatAssignments.forEach((assignment) => {
                // Find the first available lane where this assignment doesn't overlap with existing ones
                let lane = 0;
                let conflictFound = true;

                while (conflictFound) {
                    conflictFound = lanes.some(
                        (existingLane) =>
                            existingLane.lane === lane &&
                            this.assignmentsOverlap(
                                assignment,
                                existingLane.assignment
                            )
                    );
                    if (conflictFound) lane++;
                }

                const top = 0;
                lanes.push({
                    assignment: assignment,
                    lane,
                    height: this.ASSIGNMENT_HEIGHT,
                    top,
                    list: assignment.list,
                });
            });

            this.flatLaneAssignments = lanes;

            this.ganttLists.forEach((list: GanttList) => {
                const lanes: AssignmentLane[] = [];
                const sortedAssignments = [...list.ganttAssignments].sort(
                    (a, b) => a.startDate.getTime() - b.startDate.getTime()
                );

                sortedAssignments.forEach((assignment) => {
                    // Find the first available lane where this assignment doesn't overlap with existing ones
                    let lane = 0;
                    let conflictFound = true;

                    while (conflictFound) {
                        conflictFound = lanes.some(
                            (existingLane) =>
                                existingLane.lane === lane &&
                                this.assignmentsOverlap(
                                    assignment,
                                    existingLane.assignment
                                )
                        );
                        if (conflictFound) lane++;
                    }

                    const top = 0;
                    lanes.push({
                        assignment,
                        lane,
                        height: this.ASSIGNMENT_HEIGHT,
                        top,
                    });
                });

                this.assignmentLanes.set(list.gid, lanes);
            });
        }

        private assignmentsOverlap(
            a1: GanttAssignment,
            a2: GanttAssignment
        ): boolean {
            // Two assignments overlap if one starts before the other ends
            // We add a small buffer to prevent assignments that end/start on the same day from overlapping
            const buffer = 24 * 60 * 60 * 1000; // 1 day in milliseconds
            return (
                a1.startDate.getTime() < a2.endDate.getTime() + buffer &&
                a1.endDate.getTime() + buffer > a2.startDate.getTime()
            );
        }

        private getEarliestDate(assignments: GanttAssignment[]): Date {
            if (!assignments.length) return new Date();
            const earliest = Math.min(
                ...assignments.map((assignment) =>
                    assignment.startDate.getTime()
                )
            );
            const date = new Date(earliest);
            date.setDate(date.getDate() - 3);
            return date;
        }

        private getLatestDate(lists: Group[]): Date {
            var date = new Date();
            let assignments = lists.flatMap((list: Group) => {
                return (list.assignments || []).filter(
                    (assignment) =>
                        assignment.status === "Active" &&
                        (!!assignment.starttimestamp ||
                            !!assignment.endtimestamp)
                );
            });
            if (!assignments.length) {
                date.setDate(date.getDate() + 14);
                return date;
            }
            const latest = Math.max(
                ...assignments.map((assignment: Assignment) =>
                    new Date(
                        assignment.endtimestamp || assignment.starttimestamp
                    ).getTime()
                )
            );

            if (latest > new Date().getTime()) {
                date = new Date(latest);
            }
            var extraDays;
            switch (this.viewMode) {
                case "days":
                    extraDays = 365;
                    break;
                case "weeks":
                    extraDays = 365;
                    break;
                case "months":
                    extraDays = 365;
                    break;
                case "quarters":
                    extraDays = 365;
                    break;
            }

            date.setDate(date.getDate() + extraDays);
            return date;
        }

        private incrementDate(date: Date): void {
            switch (this.viewMode) {
                case "days":
                    date.setDate(date.getDate() + 1);
                    break;
                case "weeks":
                    date.setDate(date.getDate() + 7);
                    break;
                case "months":
                    date.setDate(1);
                    date.setMonth(date.getMonth() + 1);
                    break;
                case "quarters":
                    date.setMonth(date.getMonth() + 3);
                    break;
            }
        }

        private formatTimelineLabel(date: Date): string {
            switch (this.viewMode) {
                case "days":
                    return date.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                    });
                case "weeks":
                    return `Week ${this.getWeekNumber(
                        date
                    )} ${date.getFullYear()}`;
                case "months":
                    return date.toLocaleDateString("en-US", {
                        month: "short",
                        year: "numeric",
                    });
                case "quarters":
                    let label =
                        "Q" +
                        (Math.floor(date.getMonth() / 3) + 1) +
                        " " +
                        date.getFullYear();
                    return label;
                default:
                    return "";
            }
        }

        private getWeekNumber(date: Date): number {
            let start = new Date(date.getFullYear(), 0, 1);
            return Math.ceil(
                ((date.getTime() - start.getTime()) / 86400000 +
                    start.getDay() +
                    1) /
                    7
            );
        }

        private isSameDay(date1: Date, date2: Date): boolean {
            return date1.toDateString() === date2.toDateString();
        }

        private isSameDate(date1: Date, date2: Date): boolean {
            switch (this.viewMode) {
                case "days":
                    return this.isSameDay(date1, date2);
                case "weeks":
                    return (
                        this.getWeekNumber(date1) ===
                            this.getWeekNumber(date2) &&
                        date1.getFullYear() === date2.getFullYear()
                    );
                case "months":
                    return (
                        date1.getMonth() === date2.getMonth() &&
                        date1.getFullYear() === date2.getFullYear()
                    );
                case "quarters":
                    return (
                        Math.floor(date1.getMonth() / 3) + 1 ===
                            Math.floor(date2.getMonth() / 3) + 1 &&
                        date1.getFullYear() === date2.getFullYear()
                    );
                default:
                    return false;
            }
        }

        // Public methods for template
        public getAssignmentBarStyle(
            assignment: GanttAssignment,
            lane: AssignmentLane
        ): any {
            if (!this.timeline.length) return { display: "none" };

            // Handle regular dated assignments
            const startIndex = this.timeline.findIndex((unit) =>
                this.isSameDate(unit.date, assignment.startDate)
            );

            var endIndex;
            if (this.timelineEnd.getTime() == assignment.endDate.getTime()) {
                endIndex = this.timeline.length - 1;
            } else {
                endIndex = this.timeline.findIndex((unit) =>
                    this.isSameDate(unit.date, assignment.endDate)
                );
            }

            if (startIndex === -1) {
                return { display: "none" };
            }

            if (endIndex === -1 || endIndex < startIndex) {
                endIndex = startIndex;
            }

            const duration = endIndex - startIndex + 1;
            const left = startIndex * this.cellWidth;
            const width = duration * this.cellWidth - 1; // Subtract 1px for border/gap

            return {
                left: `${left}px`,
                width: `${width}px`,
                height: `${this.ASSIGNMENT_HEIGHT}px`,
                top: `${lane.top + 8}px`, // Add 8px offset from container padding
                position: "absolute",
                backgroundColor: this.getStatusColor(assignment.status),
                zIndex: 1,
            };
        }
        // Public methods for template
        public getListBarStyle(list: GanttList): any {
            if (!this.timeline.length) return { display: "none" };
            // Find the earliest start date and latest end date from all assignments
            let earliestStart: Date | null = null;
            let latestEnd: Date | null = null;

            for (const assignment of list.ganttAssignments) {
                if (
                    !earliestStart ||
                    assignment.startDate.getTime() < earliestStart.getTime()
                ) {
                    earliestStart = assignment.startDate;
                }
                if (
                    !latestEnd ||
                    assignment.endDate.getTime() > latestEnd.getTime()
                ) {
                    latestEnd = assignment.endDate;
                }
            }

            // Handle regular dated assignments
            const startIndex = this.timeline.findIndex((unit) =>
                this.isSameDate(unit.date, earliestStart)
            );

            var endIndex;
            if (this.timelineEnd.getTime() == latestEnd.getTime()) {
                endIndex = this.timeline.length - 1;
            } else {
                endIndex = this.timeline.findIndex((unit) =>
                    this.isSameDate(unit.date, latestEnd)
                );
            }

            if (startIndex === -1) {
                return { display: "none" };
            }

            if (endIndex === -1 || endIndex < startIndex) {
                endIndex = startIndex;
            }

            const duration = endIndex - startIndex + 1;
            const left = startIndex * this.cellWidth;
            const width = duration * this.cellWidth - 1; // Subtract 1px for border/gap

            return {
                left: `${left}px`,
                width: `${width}px`,
                height: `${this.ASSIGNMENT_HEIGHT}px`,
                top: `8px`,
                position: "absolute",
                backgroundColor: this.getStatusColor("List"),
                zIndex: 1,
            };
        }
        public getAssignmentProgressStyle(assignment: GanttAssignment): any {
            return {
                width: `${assignment.progress}%`,
                backgroundColor: this.getProgressColor(assignment.status),
                opacity: 0.3,
            };
        }

        public getAssignmentLanes(listId: string): AssignmentLane[] {
            return this.assignmentLanes.get(listId) || [];
        }

        public getStatusColor(status: string): string {
            const colors = {
                Active: "#15b26d",
                Done: "#2c7297",
                Scheduled: "#ffbf0f",
                List: "#7820c6",
            };
            return colors[status] || "#3498db";
        }

        public getProgressColor(status: string): string {
            const colors = {
                Active: "#0d7d47",
                Done: "#1c4a5d",
                Scheduled: "#cc9900",
            };
            return colors[status] || "#2874a6";
        }

        public getStatusIcon(status: string): string {
            const icons = {
                Active: "play_circle_filled",
                Done: "check_circle",
                Scheduled: "schedule",
            };
            return icons[status] || "schedule";
        }

        public onViewModeChange(
            mode: "days" | "weeks" | "months" | "quarters"
        ): void {
            this.viewMode = mode;
            if (this.ganttLists.length > 0) {
                this.generateTimeline();
                this.calculateAssignmentLanes();
            }
        }

        public getTimelineWidth(): number {
            const width = this.timeline.length * this.cellWidth;
            return width;
        }

        public formatDateRange(assignment: GanttAssignment): string {
            if (!assignment.endDate) {
                return "Ongoing";
            }

            const start = assignment.startDate.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
            });
            const end = assignment.endDate.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
            });
            return `${start} - ${end}`;
        }

        public getActiveAssignmentsCount(list: GanttList): number {
            return list.ganttAssignments.filter((assignment) => {
                return assignment.status === "Active";
            }).length;
        }

        public getCompletedAssignmentsCount(list: GanttList): number {
            return list.ganttAssignments.filter((assignment) => {
                return assignment.status === "Done";
            }).length;
        }

        public getScheduledAssignmentsCount(list: GanttList): number {
            return list.ganttAssignments.filter((assignment) => {
                return assignment.status === "Scheduled";
            }).length;
        }

        public getTotalAssignmentsCount(list?: GanttList): number {
            if (list) return list.ganttAssignments.length;
            return this.ganttLists.reduce(
                (total, list) => total + list.ganttAssignments.length,
                0
            );
        }

        public getTodayPosition(): number {
            const todayIndex = this.timeline.findIndex((unit) => unit.isToday);
            return todayIndex >= 0
                ? todayIndex * this.cellWidth +
                      this.cellWidth / 2 +
                      this.LIST_COLUMN_WIDTH
                : -1;
        }

        public isTodayInTimeline(): boolean {
            return this.timeline.some((unit) => unit.isToday);
        }

        public handleListClick(list: GanttList): void {
            if (this.lanes[list.gid]) {
                this.lanes[list.gid] = !this.lanes[list.gid];
            } else {
                this.lanes[list.gid] = true;
            }
            // this.editGroupModal(list, false, false);
        }
        public showLanes(list: GanttList): boolean {
            if (this.lanes[list.gid]) {
                return this.lanes[list.gid];
            }
            return false;
        }

        public handleAssignmentClick(
            assignment: GanttAssignment,
            ganttList: GanttList
        ): void {
            this.showAssignmentDetails(assignment, ganttList.originalList);
        }

        public deleteAssignment(assignment: GanttAssignment, ev: object): void {
            this.dialog
                .delete({
                    targetEvent: ev,
                    skipHide: true,
                    fullscreen: true,
                    locals: {
                        title: `Delete Assignment?`,
                        bodyText: "They will be permanently deleted",
                    },
                })
                .then(() => {
                    this.groupDataProvider
                        .deleteAssignment(assignment.originalAssignment)
                        .then(
                            () => {
                                this.toast.success("Assignment deleted");
                                assignment.originalAssignment.status =
                                    "Deleted";
                                this.refreshData();
                            },
                            () => this.toast.error()
                        );
                });
        }

        private editGroupModal(
            group: Group,
            assign: boolean,
            autoSplitAssign: boolean
        ): void {
            this.dialog
                .show({
                    component: "vc-create-group",
                    scope: this.$scope,
                    locals: {
                        group: group,
                        assign: assign,
                        autoSplitAssign: autoSplitAssign,
                    },
                })
                .then((newGroup: Group) => {
                    angular.extend(group, newGroup);
                    this.refreshData();
                });
        }

        public viewList(group: Group): void {
            let selectedGroup = encodeURIComponent(
                JSON.stringify({
                    gid: group.gid,
                    gname: group.gname,
                    fid: group.fid,
                })
            );

            let listView = "people";
            this.$window.location = `/#/${listView}?filter={}&group=${selectedGroup}&origin=assignments`;
        }

        private showAssignmentDetails(
            assignment: GanttAssignment,
            list: Group
        ): void {
            this.dialog
                .show({
                    component: "vc-group-assignment",
                    scope: this.$scope,
                    locals: {
                        listName: list.gname,
                        assignment: assignment.originalAssignment,
                    },
                })
                .then((edited: Assignment) => {
                    if (edited) {
                        let i: number = list.assignments.findIndex(
                            (element: Assignment) =>
                                element.id == assignment.originalAssignment.id
                        );
                        list.assignments[i] = edited;
                        list.update().then(
                            () => {
                                this.refreshData();
                                this.toast.success("Assignment updated");
                            },
                            () => this.toast.error()
                        );
                    }
                });
        }

        public addAssignment(list: Group): void {
            this.dialog
                .show({
                    component: "vc-group-assignment",
                    scope: this.$scope,
                    size: "med",
                    locals: {
                        listName: list.gname,
                        assignment: this.modelsUtils.assignment({}),
                    },
                })
                .then((assignment: Assignment) => {
                    if (assignment) {
                        list.assignments.push(assignment);
                        console.log(list);
                        list.update().then(
                            () => this.toast.success("Assignment added"),
                            () => this.toast.error()
                        );
                    }
                });
        }

        public trackByListId(index: number, list: GanttList): string {
            return list.gid || `list-${index}`;
        }

        public trackByAssignmentId(
            index: number,
            lane: AssignmentLane
        ): string {
            return lane.assignment.id || `assignment-${index}`;
        }

        public getFilteredLists(): GanttList[] {
            return this.ganttLists.filter((list) => list.gstatus !== "Done");
        }

        public areFiltersApplied(): boolean {
            const filters = this.filters;
            return (
                filters.teams.length > 0 ||
                filters.users.length > 0 ||
                !!filters.dateRange ||
                filters.status.length > 0
            );
        }

        public getListDateRange(list: GanttList): string {
            if (!list.ganttAssignments || list.ganttAssignments.length === 0) {
                return "";
            }

            const startDates = list.ganttAssignments.map((a) =>
                a.startDate.getTime()
            );
            const endDates = list.ganttAssignments.map((a) =>
                a.endDate.getTime()
            );

            const earliestStart = new Date(Math.min(...startDates));
            const latestEnd = new Date(Math.max(...endDates));

            const startStr = earliestStart.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
            });
            const endStr = latestEnd.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
            });

            return `${startStr} - ${endStr}`;
        }

        public openFiltersDialog(ev: any): void {
            if (!this.filters) {
                this.filters = this.modelsUtils.assignmentFilter({});
            }
            let filters: AssignmentFilter = this.filters;
            this.dialog
                .show({
                    component: "vc-assignment-filters-dialog",
                    targetEvent: ev,
                    scope: this.$scope,
                    clickOutsideToClose: false,
                    ariaLabel: "Assignment Filters",
                    locals: {
                        filters: angular.copy(filters),
                    },
                })
                .then((filters: AssignmentFilter) => {
                    this.filters.setData(filters);
                    this.saveLocalStorage();
                    this.refreshData();
                });
        }
        deleteList(list: Group, ev: object): void {
            this.dialog
                .delete({
                    targetEvent: ev,
                    skipHide: true,
                    fullscreen: true,
                    locals: {
                        title: `Delete List?`,
                        bodyText: "They will be permanently deleted",
                    },
                })
                .then(() => {
                    this.groupDataProvider.removeGroup(list).then(
                        () => {
                            this.toast.success("List deleted");
                            this.refreshData();
                        },
                        () => this.toast.error()
                    );
                });
        }
        approveList(list: Group, ev: object): void {
            list.gstatus = "Active";
            list.update().then(
                () => {
                    this.toast.success("List approved");
                    this.refreshData();
                },
                () => this.toast.error()
            );
        }
        getFilters(): void {
            if (this.localStorageService.isSupported && this.campaignid) {
                let localInfo = this.localStorageService.get(
                    "gantt-" + this.campaignid
                );
                if (!!localInfo && localInfo !== "null") {
                    this.localInfo = localInfo;
                    if (!!localInfo["filters"]) {
                        this.filters = this.modelsUtils.assignmentFilter(
                            localInfo["filters"]
                        );
                    }
                    this.viewMode = localInfo["viewMode"] ?? "days";
                    return;
                }
            }
            this.localInfo = {};
            this.filters = this.modelsUtils.assignmentFilter({});
            this.viewMode = "days";
        }

        saveLocalStorage(): void {
            if (this.localStorageService.isSupported) {
                this.localInfo = {
                    filters: this.filters,
                    viewMode: this.viewMode,
                };
                this.localStorageService.set(
                    "gantt-" + this.campaignid,
                    this.localInfo
                );
            }
        }
    },
};

angular
    .module("planner")
    .component(GanttChartComponent.selector, GanttChartComponent);
